import ActionsDropdown from 'components/ActionsDropdown'
import {
  Settings,
  Bell,
  LogOut,
  Plus,
  UserRoundPlus,
  Link,
  TriangleAlert,
} from 'lucide-react'
import { Group } from '@wellesley/openapi'
import { useMemo } from 'react'
import { toast } from '@wellesley/system-toast'

import useGroupActions from '../../../hooks/useGroupActions'
import useLeaveGroup from '../../../hooks/useLeaveGroup'
import { GroupActionConfig } from '../../../types/group-actions'
import InviteModal from '../../modals/InviteModal'
import { getRoute } from '@wellesley/router'
import { useEventSwitchState } from 'hooks/state/useEventState'
import { useCopyGroupUrl } from './hooks/useCopyGroupUrl'

type ManageGroupProps = {
  group: Group
  desktopModalBehavior?: boolean
  trigger?: React.ReactNode
  isPortal?: boolean
  triggerClass?: string
  onOpenChange?: (open: boolean) => void
  onInviteOpenChange?: (open: boolean) => void
  align?: 'start' | 'end'
  alignOffset?: number
}

const iconSize = 20
const ManageGroup = ({
  group,
  desktopModalBehavior = true,
  isPortal = true,
  trigger,
  triggerClass,
  onOpenChange,
  onInviteOpenChange,
  align = 'start',
  alignOffset,
}: ManageGroupProps) => {
  const { leaveGroup, LeaveGroupDialog } = useLeaveGroup({
    groupId: group.groupId,
  })

  const [isInviteOpen, openInviteModal, closeInviteModal] = useEventSwitchState(
    false,
    onInviteOpenChange,
  )

  const copyGroupUrl = useCopyGroupUrl(group.groupName)

  const actionConfigs: GroupActionConfig[] = useMemo(
    () => [
      {
        action: {
          name: 'Report',
          icon: <TriangleAlert size={iconSize} />,
          handler: () => {
            toast.error('Report group functionality coming soon')
          },
        },
        shouldShow: ({ isOwner, isMember }) => !isOwner && isMember,
      },
      {
        action: {
          name: 'Invite',
          icon: <Plus size={iconSize} />,
          handler: () => {
            openInviteModal()
          },
        },
        shouldShow: ({ permissions }) => permissions.canInviteGroupMembers,
      },
      {
        action: {
          name: 'Copy link',
          icon: <Link size={iconSize} />,
          handler: () => {
            copyGroupUrl()
          },
        },
        shouldShow: () => true,
      },
      {
        action: {
          name: 'View join requests',
          icon: <UserRoundPlus size={iconSize} />,
          link: getRoute('/g/:groupname/settings/:tab?', {
            groupname: group.groupName,
            tab: 'requests',
          }),
        },
        shouldShow: ({ permissions }) => permissions.canManageGroupRequests,
      },
      {
        action: {
          name: 'Manage notifications',
          icon: <Bell size={iconSize} />,
          handler: () => {
            toast.error('Manage notification functionality coming soon')
          },
        },
        shouldShow: ({ permissions }) => permissions.canManageNotifications,
      },
      {
        action: {
          name: 'Leave group',
          className: 'text-red-600',
          icon: <LogOut size={iconSize} />,
          handler: () => {
            leaveGroup()
          },
        },
        shouldShow: ({ isOwner, isMember }) => !isOwner && isMember,
      },
    ],
    [group.groupName, leaveGroup, copyGroupUrl, openInviteModal],
  )

  const { actions } = useGroupActions(group, actionConfigs)

  if (actions.length === 0) {
    return null
  }

  return (
    <>
      <ActionsDropdown
        actions={actions}
        onOpenChange={onOpenChange}
        desktopModalBehavior={desktopModalBehavior}
        mobileBehavior="dropdown"
        dropdownClassName="w-[288px]"
        align={align}
        alignOffset={alignOffset}
        isPortal={isPortal}
        triggerVariant="none"
        triggerSize="icon_xs"
        triggerClass={
          triggerClass ||
          'flex rounded-m items-center justify-center border-gray-700 px-xs py-xxs focus-visible:ring-0 focus-visible:ring-transparent'
        }
        trigger={
          trigger || (
            <>
              <Settings size={20} className="mr-xxs" />
              Manage group
            </>
          )
        }
      />
      <LeaveGroupDialog />
      {isInviteOpen && (
        <InviteModal
          isOpen={isInviteOpen}
          onClose={closeInviteModal}
          groupId={group.groupId}
        />
      )}
    </>
  )
}

export default ManageGroup
